import { useEffect, useMemo, useState } from "react";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { withErrorBoundary } from "../../../ErrorBoundary";
import { ArrowDownIcon } from "../../../Icon";
import { Error } from "../Error";
import { Help } from "../Help";
import { Label, labelSizes, labelVariants } from "../Label";
import { Suffix, suffixSizes } from "../Suffix";
import styles from "./Select.style";

const SelectStyled = styled.div((props) => ({ ...styles(props) }));

const Select = ({
    placeholder,
    options,
    labelAlignLeft,
    labelWidth,
    labelComponent,
    labelIconComponent,
    labelIsMobileOnly,
    name,
    id,
    value,
    label,
    onChange,
    onFocus,
    onBlur,
    required,
    disabled,
    error,
    errorMessage,
    helpText,
    variant,
    size,
    fieldWidth,
    inputWidth,
    suffix,
    suffixWidth,
    className,
    ...rest
}) => {
    const [focused, setFocused] = useState(false);
    const uid = useUIDSeed();

    // Blur won't fire when the disabled state is set on a focused input.
    useEffect(() => {
        if (disabled && focused) {
            setFocused(false);
            if (onBlur) {
                onBlur();
            }
        }
    }, [disabled, focused, onBlur]);

    // onFocus
    const handleFocus = (event) => {
        if (disabled) {
            event.stopPropagation();
            return;
        }
        if (onFocus) {
            onFocus(event);
        }
        setFocused(true);
    };

    // onBlur
    const handleBlur = (event) => {
        if (onBlur) {
            onBlur(event);
        }
        setFocused(false);
    };

    // onChange
    const handleChange = (event, ...args) => {
        if (onChange) {
            onChange(event, ...args);
        }
    };

    const selectOptions = useMemo(
        () => (placeholder ? [{ label: placeholder, value: "" }, ...options] : options),
        [options],
    );

    if (!selectOptions?.length) {
        return null;
    }

    return (
        <SelectStyled
            labelAlignLeft={labelAlignLeft}
            labelWidth={labelWidth}
            focused={focused}
            disabled={disabled}
            error={error}
            variant={variant}
            size={size}
            fieldWidth={fieldWidth}
            inputWidth={inputWidth}
            className={className}
            {...rest}
        >
            <Label
                className="label"
                label={label}
                labelComponent={labelComponent}
                iconComponent={labelIconComponent}
                id={id}
                htmlFor={id}
                error={error}
                required={required}
                variant={labelVariants.PRIMARY}
                size={labelSizes[size?.toUpperCase()]}
                width={labelWidth}
                alignLeft={labelAlignLeft}
                isMobileOnly={labelIsMobileOnly}
            />
            <div className="inputContainer">
                <div className="inputContainer_inner">
                    <div className="inputWrap">
                        <select
                            name={name}
                            aria-invalid={!!error}
                            id={id}
                            value={value}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            required={required}
                            disabled={disabled}
                        >
                            {selectOptions?.map((option) => (
                                <option
                                    key={uid(option?.label)}
                                    value={option?.value}
                                    disabled={option?.disabled}
                                >
                                    {option?.label}
                                </option>
                            ))}
                        </select>
                        <span className="selectArrow">
                            <ArrowDownIcon viewBox="0 0 14 8" />
                        </span>
                    </div>
                    <Suffix
                        label={suffix}
                        size={suffixSizes[size?.toUpperCase()]}
                        width={suffixWidth}
                        error={error}
                    />
                </div>
                <Error error={error} errorMessage={errorMessage} />
                <Help text={helpText} />
            </div>
        </SelectStyled>
    );
};

const selectVariants = Object.freeze({
    PRIMARY: "primary",
    SECONDARY: "secondary",
    TERTIARY: "tertiary",
});

const selectSizes = Object.freeze({
    SMALL: "small",
    NORMAL: "normal",
    LARGE: "large",
});

Select.propTypes = {
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    labelAlignLeft: PropTypes.bool,
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelComponent: PropTypes.node,
    labelIconComponent: PropTypes.node,
    labelIsMobileOnly: PropTypes.bool,
    name: PropTypes.string,
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    helpText: PropTypes.string,
    variant: PropTypes.oneOf([...Object.values(selectVariants)]),
    size: PropTypes.oneOf([...Object.values(selectSizes)]),
    fieldWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    suffix: PropTypes.string,
    suffixWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
};

Select.defaultProps = {
    placeholder: undefined,
    options: undefined,
    labelAlignLeft: false,
    labelWidth: undefined,
    labelComponent: undefined,
    labelIconComponent: undefined,
    labelIsMobileOnly: false,
    name: undefined,
    id: undefined,
    value: undefined,
    label: undefined,
    onChange: () => {},
    onFocus: () => {},
    onBlur: () => {},
    required: false,
    disabled: false,
    error: false,
    errorMessage: undefined,
    helpText: undefined,
    variant: selectVariants.PRIMARY,
    size: selectSizes.NORMAL,
    fieldWidth: undefined,
    inputWidth: undefined,
    suffix: undefined,
    suffixWidth: undefined,
    className: undefined,
};

export { selectVariants, selectSizes };

export default withErrorBoundary(Select);
